import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import {
  ReportTypeGQLType,
  useCancelFundingRequestMutation,
  useCreateServicerReportPreviewMutation,
  useGetFundingRequestQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  CancelFundingRequestMenuItem,
  useCanCancelFundingRequest,
} from './ActionsMenuItem/CancelFundingRequest';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';
import { useCanResubmitFundingRequest } from '~/packages/funding-requests/hooks/useCanResubmitFundingRequest';
import {
  FundingRequestContextValue,
  useFundingRequestTemplateContextV1,
} from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { useReportDownloadUrl } from '~/packages/reports/hooks/useReportDownloadUrl';

export const duplicatePermissions: PermissionsFn = (context) => {
  if (context.user.userType === 'CAPITAL_PROVIDER') {
    return context.flags.productsFundingRequestsLenderDuplicatePermissions;
  }
  return context.flags.productsFundingRequestsDuplicatePermissions;
};

// only for ReadOnly modes because should only be for funding requests that are submitted, or ones that are rejected
const ResubmitFundingRequest = ({
  template,
}: {
  template: NonNullable<FundingRequestContextValue['template']>;
}) => {
  const navigate = useNavigate();
  const {
    creditFacility,
    context: { mode, allowedActions },
  } = useFundingRequestTemplateContextV1();

  const { data, loading: isLoading } = useGetFundingRequestQuery({
    variables: {
      fundingRequestId:
        template?.currentFundingRequestComponents[0].fundingRequestId || '',
    },
  });

  const navigateToResubmit = useCallback(() => {
    if (mode === 'create') {
      return navigate(
        `/funding-requests/create?creditFacilityId=${creditFacility.id}&fundingRequestIdToDuplicate=${template?.currentFundingRequestComponents[0].fundingRequestId}`,
        { replace: true, state: { from: 'resubmit funding request' } },
      );
    }
    if (mode === 'upsize') {
      finleyAssert(
        template.loans && template.loans.length === 1,
        'Request must contain 1 loan for upsizing',
      );
      const loan = template.loans[0];
      return navigate(
        `/funding-requests/upsize?creditFacilityId=${creditFacility.id}&loanId=${loan.id}&fundingRequestIdToDuplicate=${template?.currentFundingRequestComponents[0].fundingRequestId}`,
        { replace: true, state: { from: 'resubmit funding request' } },
      );
    }

    throw Error(`${mode} mode not supported for resubmitting funding requests`);
  }, [mode, navigate, creditFacility, template]);

  const [
    cancelFundingRequestMutation,
    { loading: isCancelLoading },
  ] = useCancelFundingRequestMutation({
    onCompleted: () => {
      navigateToResubmit();
    },
  });

  if (
    (allowedActions !== 'readOnly' &&
      (mode === 'create' || mode === 'upsize')) ||
    data?.fundingRequestWithFullDetails?.status !== 'submitted'
  ) {
    return null;
  }

  return (
    <PermissionGuard permissions={duplicatePermissions}>
      <MenuItem
        as={Flex}
        gap="1rem"
        icon={<Icon icon="repeat" />}
        isDisabled={
          isLoading ||
          isCancelLoading ||
          data?.fundingRequestWithFullDetails.status !== 'submitted'
        }
        onClick={() => {
          Sentry.addBreadcrumb({
            category: 'ui.click',
            message: `Clicked resubmit funding request`,
            level: Sentry.Severity.Info,
          });

          if (data?.fundingRequestWithFullDetails.status === 'submitted') {
            cancelFundingRequestMutation({
              variables: {
                fundingRequestId: data?.fundingRequestWithFullDetails.id,
              },
            });
          }
        }}
        color="gray.800"
      >
        Resubmit Request
      </MenuItem>
    </PermissionGuard>
  );
};

const FundingRequestReport = ({
  report,
  setIsLoading,
}: {
  report: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  };
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { id: reportId, reportType, s3Path, isDomainReport } = report;
  const reportName = _.last(s3Path.split('/') as string[]);
  finleyAssert(reportName, 's3 path expected to exist as a report name');
  const displayName = _.upperFirst(_.lowerCase(reportType));

  const toast = useToast();

  const { getReportDownloadUrl, error, loading } = useReportDownloadUrl({
    toast,
    reportId,
    reportName,
    isDomainReport,
  });

  useEffect(() => {
    setIsLoading(loading);
  }, [setIsLoading, loading]);

  if (error) {
    throw error;
  }
  return (
    <MenuItem
      as={Flex}
      gap="1rem"
      icon={<Icon icon="download" />}
      onClick={() => {
        Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked download for ${reportName} in funding request`,
          level: Sentry.Severity.Info,
        });
        try {
          getReportDownloadUrl();
        } catch (err: any) {
          if (!toast.isActive(errorToastId)) {
            errorToastConfig.title = `Error getting report download for ${reportName}`;
            toast(errorToastConfig);
          }
          throw err;
        }
      }}
      color="gray.800"
    >
      {displayName}
    </MenuItem>
  );
};

// To be used on funding request details page post submission/ terminal state to download associated FR reports
const ActionsForReadOnlyButtonInner = ({
  reports,
}: {
  reports: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  }[];
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { template } = useFundingRequestTemplateContextV1();

  const { canCancel } = useCanCancelFundingRequest({
    fundingRequestId:
      template?.currentFundingRequestComponents[0].fundingRequestId || '',
  });

  const { canDuplicate } = useCanResubmitFundingRequest({
    fundingRequestId:
      template?.currentFundingRequestComponents[0].fundingRequestId || '',
  });

  const hasReports = reports.length > 0;
  const noAvailableItems = !hasReports && !canDuplicate && !canCancel;

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="secondary"
        colorScheme="v2-aurora"
        rightIcon={<Icon icon="chevron-down" />}
        isLoading={isLoading}
      >
        Actions
      </MenuButton>
      <MenuList>
        {hasReports &&
          _.map(reports, (report) => (
            <FundingRequestReport
              key={`funding-request-report-${report.id}`}
              report={report}
              setIsLoading={setIsLoading}
            />
          ))}

        {!!template && (
          <>
            <ResubmitFundingRequest template={template} />
            <CancelFundingRequestMenuItem
              fundingRequestId={
                template.currentFundingRequestComponents[0].fundingRequestId ||
                ''
              }
            />
          </>
        )}

        {noAvailableItems && (
          <MenuItem isDisabled>No actions available</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

const ActionsForMutableButtonInner = () => {
  // @jendevelops TODO: update to use reporting mechanics to determine if show or hide servicer report
  const { isSlsAgentLender } = useFeatureFlags();
  const toast = useToast();
  const {
    context: { submission },
  } = useFundingRequestTemplateContextV1();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [createServicerReportPreview] = useCreateServicerReportPreviewMutation({
    onCompleted: () => {
      toast({
        title:
          'Creating servicing report preview, please check your email in a few minutes.',
        status: 'success',
      });
      setIsLoading(false);
    },
  });

  const handlePreviewRequest = useCallback(() => {
    setIsLoading(true);

    const submissionPayload = submission.getPayload();

    if (!submissionPayload.isValid) {
      setIsLoading(false);
      toast({
        position: 'top',
        status: 'error',
        isClosable: true,
        title: `Error creating servicing report preview.`,
      });
      return;
    }

    createServicerReportPreview({
      variables: {
        fundingRequestChangeset: {
          ...submissionPayload.payload,
        },
      },
    });
  }, [createServicerReportPreview, setIsLoading, submission, toast]);

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="secondary"
        colorScheme="v2-aurora"
        rightIcon={<Icon icon="chevron-down" />}
        isLoading={isLoading}
      >
        Actions
      </MenuButton>
      <MenuList>
        {submission.getPayload().isValid && !isSlsAgentLender && (
          <MenuItem
            icon={<Icon icon="paper-plane-top" />}
            onClick={() => {
              Sentry.addBreadcrumb({
                category: 'ui.click',
                message: `Clicked generate servicer report preview in funding request`,
                level: Sentry.Severity.Info,
              });
              handlePreviewRequest();
            }}
            color="gray.800"
          >
            Email Servicer Report
          </MenuItem>
        )}
        {(!submission.getPayload().isValid || isSlsAgentLender) && (
          <MenuItem isDisabled>No actions available</MenuItem>
        )}
      </MenuList>
    </Menu>
  );
};

// To be used on the create funding request page
const ActionsForMutableButton = () => {
  const {
    context: { allowedActions },
  } = useFundingRequestTemplateContextV1();

  return allowedActions === 'mutable' ? <ActionsForMutableButtonInner /> : null;
};

export const ActionsButton = ({
  reports = [],
}: {
  reports?: {
    id: string;
    s3Path: string;
    reportType: ReportTypeGQLType;
    isDomainReport: boolean;
  }[];
}) => {
  const {
    context: { allowedActions },
  } = useFundingRequestTemplateContextV1();

  return allowedActions === 'readOnly' ? (
    <ActionsForReadOnlyButtonInner reports={reports} />
  ) : (
    <ActionsForMutableButton />
  );
};
